import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  getGetParticipantMeParticipantsMeGetQueryKey,
  getGetTaxFormsTaxUsFormsGetQueryKey,
  useCreateTaxFormTaxUsFormsPost,
} from "@/api-v3";
import { CardContainer } from "@/components/card-container";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useErrorHandler } from "@/hooks/use-error-handler";
import { usePageTitle } from "@/hooks/use-title";
import { openLink } from "@/lib/routing";

enum Delivery {
  paper = "paper",
  electronic = "electronic",
}

export const CreateTaxFormPage = () => {
  const { t } = useTranslation();
  const [deliveryMethod, setDeliveryMethod] = useState<Delivery>(
    Delivery.electronic
  );
  const { handleMutationError } = useErrorHandler();
  usePageTitle(t("tax_form_title"));
  const queryClient = useQueryClient();
  const { mutate, isPending: isLoading } = useCreateTaxFormTaxUsFormsPost({
    mutation: {
      onError: (error) => {
        handleMutationError(error);
      },
      onSuccess: (data) => {
        const url = data.data.form_url;
        queryClient.removeQueries({
          queryKey: getGetTaxFormsTaxUsFormsGetQueryKey(),
        });
        // Drop the cached compliance answer too, so returning from the form
        // shows nothing until a fresh /participants/me response, not a stale
        // "tax info required" alert.
        queryClient.removeQueries({
          queryKey: getGetParticipantMeParticipantsMeGetQueryKey(),
        });
        openLink(url, "_self");
      },
    },
  });

  const handleSubmit = () => {
    mutate({
      data: {
        delivery_method: deliveryMethod,
      },
    });
  };

  function radioComponent(value: Delivery, title: string, description: string) {
    return (
      <Label
        className="cursor-pointer"
        onClick={() => setDeliveryMethod(value)}
      >
        <div className="border p-4 rounded-xl h-full">
          <div className="flex gap-4 items-center">
            <RadioGroupItem value={value} id={value} />
            <h6 className="text-xl font-semibold">{title}</h6>
          </div>

          <div className="mt-2">
            <p className="text-sm">{description}</p>
          </div>
        </div>
      </Label>
    );
  }

  return (
    <CardContainer>
      <CardContainer.Content>
        <div>
          <fieldset className="mb-6 mt-4">
            <legend className="text-muted-foreground">
              {t("tax_choose_delivery_method")}
            </legend>
            <RadioGroup
              className="mt-4"
              value={deliveryMethod}
              onValueChange={(value) => setDeliveryMethod(value as Delivery)}
            >
              <div className="flex flex-col md:flex-row gap-4 min-w-0">
                <div className="flex-1 w-full md:w-[calc(50%-8px)]">
                  {radioComponent(
                    Delivery.electronic,
                    t("tax_electronic"),
                    t("tax_electronic_description")
                  )}
                </div>
                <div className="flex-1 w-full md:w-[calc(50%-8px)]">
                  {radioComponent(
                    Delivery.paper,
                    t("tax_paper"),
                    t("tax_paper_description")
                  )}
                </div>
              </div>
            </RadioGroup>
          </fieldset>
        </div>

        <div className="flex justify-end mt-4">
          <Button type="submit" loading={isLoading} onClick={handleSubmit}>
            {t("continue_text")}
          </Button>
        </div>
      </CardContainer.Content>
    </CardContainer>
  );
};
